// Spelman College: public college page (#2652), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Spelman's Common Data Set 2025-2026
//    (cds_2025-2026_spelman_college_1.26.2026.pdf), linked from Institutional
//    Research's Common Data Set page: C1 applicants, admits and enrollees for
//    Fall 2025 (all reported as women), C21/C22 early decision and early
//    action counts. The C1 residency table fills only the enrolled row, so
//    the page shows no residency table.
//  - spelman.edu Admissions: First-Time Student Applicant (the three plans
//    and deadlines; the page says it is accepting applications for Fall
//    2027), Test Optional Admissions Policy, and the Admissions FAQ (when
//    early action and regular decision applicants hear back; Spelman does not
//    publish an early decision notification date).
//  - Financial Aid Office, 2026-2027 Cost of Attendance (first-year students,
//    on campus), checked against the Student Account Center's 2026-2027
//    Tuition and Fees chart ($15,021 tuition and $1,783 fees per semester).
//  - Spelman's Majors and CIP Codes list (Institutional Research, updated
//    7/13/2026), for the majors file.
//  - College Scorecard, school id 141060 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Quirks: the CDS answers "No" to C8A (whether Spelman uses SAT or ACT scores
// in admission) and checks no fall 2027 policy box, yet gives a February 15
// score deadline; the admissions office says it is test-optional and reviews
// and superscores scores that are sent. The page follows the admissions
// office. The CDS G1 lists 2026-2027 tuition of $29,025, fees of $3,445 and
// food and housing of $18,924, which do not match the 2026-2027 figures the
// financial aid office and Student Account Center publish; the page uses the
// financial aid office's cost of attendance, without its $804 loan-fee
// allowance, so its total ($60,879) is $804 below that page's $61,683.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Spelman Common Data Set 2025-2026',
        url: 'https://www.spelman.edu/about/institutional-research/common-data-set/index.html',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'Spelman Admissions, First-Time Student Applicant',
        url: 'https://www.spelman.edu/admissions/applying-to-spelman/first-time-student-applicant.html',
        read: '2026-10-01',
    },
    faq: {
        label: 'Spelman Admissions, Frequently Asked Questions',
        url: 'https://www.spelman.edu/admissions/frequently-asked-questions.html',
        read: '2026-10-01',
    },
    tests: {
        label: 'Spelman Admissions, Test Optional Admissions Policy',
        url: 'https://www.spelman.edu/admissions/applying-to-spelman/test-optional-admissions-policy.html',
        read: '2026-10-01',
    },
    coa: {
        label: 'Spelman Financial Aid, 2026-2027 Cost of Attendance',
        url: 'https://www.spelman.edu/financial-aid/cost-of-attendance.html',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?141060-Spelman-College',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'spelman-college',
    kbId: 'spelman_college',
    name: 'Spelman College',
    shortName: 'Spelman',
    location: 'Atlanta, GA',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Spelman web colors); decoration only.
    branding: {
        primary: '#92C1E9',
        secondary: '#FFFFFF',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/spelman_college_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 11533,
        admitted: 2743,
        enrolled: 648,
        source: 'cds',
        where: 'section C1',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Spelman\'s Common Data Set reports one first-year acceptance rate for the whole college, with no rate by major.',
        source: 'cds',
    },
    earlyRounds: {
        year: 'Fall 2025',
        title: 'Early decision and early action',
        note: 'Counts for the class that entered in fall 2025.',
        rows: [
            { plan: 'Early Decision', applied: 437, admitted: 70 },
            { plan: 'Early Action', applied: 5282, admitted: 1599 },
        ],
        source: 'cds',
        where: 'sections C21 and C22',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'February 1' },
        rows: [
            { plan: 'Early Decision', deadline: 'November 1', decision: null, binding: true },
            { plan: 'Early Action', deadline: 'November 1', decision: 'By December 31', binding: false },
            { plan: 'Regular Decision', deadline: 'February 1', decision: 'By April 1', binding: false },
        ],
        note: 'Supporting documents are due within five business days of each deadline; incomplete early applications move to Regular Decision. Decision dates are from the admissions FAQ, which gives none for early decision. Admitted Early Action and Early Decision students are invited to apply to the Honors program; Regular Decision and deferred applicants are not.',
        source: 'firstYear',
        binding: 'Early Decision is binding and suits an applicant whose clear first choice is Spelman: if accepted, she withdraws all other applications. An applicant seeking financial aid is not required to withdraw other applications, but must pay the enrollment deposit by May 1. Early Action is not binding.',
        bindingSource: 'firstYear',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Spelman is test-optional for early decision, early action and regular decision. Applying without scores is not a disadvantage, and you do not have to say why. If you send SAT or ACT scores, they are reviewed as part of the whole application, and Spelman superscores both tests.',
        source: 'tests',
        cdsLine: 'The Common Data Set answers "No" to whether Spelman uses SAT or ACT scores in admission decisions and marks no fall 2027 policy, while listing February 15 as the date scores must arrive; the admissions office\'s policy above is the current one.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'faq',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 30042 },
            { item: 'Required fees', amount: 3566 },
            { item: 'Housing (on campus)', amount: 11994 },
            { item: 'Food (meal plan)', amount: 8114 },
            { item: 'Books and supplies', amount: 1500 },
            { item: 'Transportation', amount: 1500 },
            { item: 'Personal expenses', amount: 2000 },
            { item: 'Health insurance', amount: 1758 },
            { item: 'New student fees', amount: 405 },
        ],
        total: 60879,
        note: 'For a first-year student living on campus, fall and spring.',
        source: 'coa',
        netPrice: {
            average: 46089,
            byIncome: [
                { income: '$0 to $30,000', amount: 41125 },
                { income: '$30,001 to $48,000', amount: 41146 },
                { income: '$48,001 to $75,000', amount: 44626 },
                { income: '$75,001 to $110,000', amount: 47999 },
                { income: 'Over $110,000', amount: 50714 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Spelman\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
